/**
 * Utility functions for handling multi-language translations
 */

/**
 * Gets the translated value for a given language key
 * Falls back to Spanish if translation not found
 * @param {Object|string} value - Either a translation object or a plain string
 * @param {string} langKey - Language key (es, en, pt, u)
 * @returns {string} The translated string
 */
export const getTranslatedValue = (value, langKey) => {
  // If it's already a string, return it
  if (typeof value === 'string' || !value) {
    return value || '';
  }

  // If it's an object with translations
  if (typeof value === 'object') {
    // Try the requested language
    if (value[langKey]) {
      return value[langKey];
    }

    // Fallback to Spanish
    if (value.es) {
      return value.es;
    }

    // If no translations found, try to return the first available
    const keys = Object.keys(value);
    if (keys.length > 0) {
      return value[keys[0]];
    }
  }

  return '';
};

/**
 * Creates a translation object from a single string value
 * @param {string} value - The value to use for all languages
 * @returns {Object} Translation object with the value for all supported languages
 */
export const createTranslationObject = (value) => {
  if (!value) return {};

  return {
    es: value,
    en: value,
    pt: value,
    u: value,
  };
};

/**
 * Checks if a value has translations
 * @param {any} value - The value to check
 * @returns {boolean} True if the value has translations
 */
export const hasTranslations = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Ensures a value is a translation object
 * If it's a string, converts it to a translation object
 * @param {any} value - The value to ensure
 * @param {string} defaultLang - Default language for string values
 * @returns {Object} Translation object
 */
export const ensureTranslationObject = (value, defaultLang = 'es') => {
  if (!value) return {};

  if (typeof value === 'string') {
    return {
      [defaultLang]: value,
    };
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }

  return {};
};
